import { useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import { bytesToHexLower, type TenantConfig, type TokenDecoded } from '@pink/shared';

import { AppShell } from '../../ui-module/app-shell.js';
import { ScreenShell } from '../../ui-module/screen-shell.js';
import { BootstrapSkeleton } from '../../ui-module/bootstrap-skeleton.js';
import { TechnicalErrorScreen } from '../screens-feature/technical-error-screen.js';
import { NotFoundScreen } from '../screens-feature/not-found-screen.js';
import { TechnicalErrorVariant } from '../technical-error-variant.js';
import {
  useCancelMachine,
  type CancelMachine,
} from '../state-machine-feature/use-cancel-machine.js';
import { PinningProvider } from '../../pinning-module/pinning-context.js';
import { usePinningBootstrap } from '../../pinning-module/use-pinning-bootstrap.js';
import { useLanguage } from '../../i18n-module/use-language.js';
import type { PinningVerifier, NowFn } from './cancel-routes.type.js';
import { useCancelRouteBootstrap } from '../submit-feature/use-cancel-route-bootstrap.js';
import { useTokenFromRoute } from '../../token-module/use-token-from-route.js';
import { PatientKeypairProvider } from '../polling-feature/patient-keypair-context.js';
import { useLoadKeypairIntoContext } from '../polling-feature/use-load-keypair-into-context.js';
import { useEndStateCleanup } from '../polling-feature/use-end-state-cleanup.js';
import { useRecoveryBootstrap } from '../recovery-feature/use-recovery-bootstrap.js';
import { AnimatedScreens } from '../screens-feature/render-screen.js';
import {
  useRecoveryAutoDispatch,
  usePollingForLoadingScreen,
  usePinningErrorDispatch,
} from '../state-machine-feature/use-auto-dispatch.js';

export interface PinnedAppProps {
  readonly config: TenantConfig;
  readonly verifyPinning?: PinningVerifier;
  readonly now?: NowFn;
}

interface CancelAppProps {
  readonly machine: CancelMachine;
  readonly now: NowFn;
  readonly tenantId: string;
}

interface PinningGateProps {
  readonly config: TenantConfig;
  readonly machine: CancelMachine;
  readonly decoded: TokenDecoded;
  readonly fingerprintHex: string;
  readonly now: NowFn;
  readonly verifyPinning?: PinningVerifier;
}

/**
 * Innerer App-Inhalt nach erfolgreichem Pinning: hängt Token-Bootstrap,
 * Recovery-Bootstrap, Polling-Loop und End-State-Cleanup ein. Rendert die
 * State-Machine in der `AppShell` (Page-Gradient + TenantHeader) mit
 * AnimatePresence-gebundenen ScreenShells.
 *
 * Reihenfolge der Effekte (DEV-017):
 *  - `useRecoveryBootstrap` läuft einmal und entscheidet, ob ein gültiger
 *    IDB-Eintrag wiedergefunden wurde.
 *  - `useRecoveryAutoDispatch` schickt bei `recovered` einmalig
 *    `GOTO_LOADING` an den Reducer.
 *  - `useLoadKeypairIntoContext` lädt im LOADING-Screen den Eintrag aus IDB
 *    in den Keypair-Context — deckt Submit- und Recovery-Pfad ab.
 *  - `usePollingForLoadingScreen` startet den 5-Sekunden-Loop, sobald
 *    LOADING und Context-Eintrag zusammenfallen.
 *  - `useEndStateCleanup` leert die IDB beim Übergang in einen End-State.
 */
function CancelApp(props: CancelAppProps): JSX.Element {
  const { machine, now, tenantId } = props;
  const language = useLanguage();
  useCancelRouteBootstrap({
    dispatch: machine.dispatch,
    locale: language.locale,
    now: now(),
  });
  const recovery = useRecoveryBootstrap({ now });
  useRecoveryAutoDispatch(recovery, machine);
  useLoadKeypairIntoContext({ enabled: machine.state.screen === 'LOADING', now });
  usePollingForLoadingScreen(machine);
  useEndStateCleanup(machine.state.screen);
  return (
    <AppShell>
      <AnimatedScreens machine={machine} tenantId={tenantId} />
    </AppShell>
  );
}

/**
 * Token-Decode hat fehlgeschlagen → einmaliger Dispatch `TOKEN_INVALID`,
 * dann state-machine-gesteuerter Render (Technical-Error generic) innerhalb
 * der AppShell.
 */
function TokenInvalidGate(props: {
  readonly machine: CancelMachine;
  readonly tenantId: string;
}): JSX.Element {
  const { dispatch } = props.machine;
  useEffect(() => {
    dispatch({ type: 'TOKEN_INVALID' });
  }, [dispatch]);
  return (
    <AppShell>
      <AnimatedScreens machine={props.machine} tenantId={props.tenantId} />
    </AppShell>
  );
}

/**
 * Führt Pinning gegen den Fingerprint aus dem dekodierten Token aus. Bei
 * Fehler dispatched einmalig `PINNING_FAILED` und zeigt den Pinning-Failure-
 * Screen. Bei Erfolg rendert die regulären `<CancelApp>`-Provider.
 */
function PinningGate(props: PinningGateProps): JSX.Element {
  const pinning = usePinningBootstrap({
    config: props.config,
    expectedFingerprintHex: props.fingerprintHex,
    ...(props.verifyPinning ? { verify: props.verifyPinning } : {}),
  });
  usePinningErrorDispatch(pinning.result, props.machine);

  if (pinning.status === 'pending') {
    return <BootstrapSkeleton />;
  }
  if (pinning.status === 'error' || pinning.result === null || !pinning.result.ok) {
    return (
      <AppShell>
        <ScreenShell motionKey={`pinning-failure-${props.config.tenantId}`}>
          <TechnicalErrorScreen variant={TechnicalErrorVariant.PinningFailure} />
        </ScreenShell>
      </AppShell>
    );
  }
  return (
    <PinningProvider verifiedKey={pinning.result.verifiedKey}>
      <PatientKeypairProvider>
        <CancelApp machine={props.machine} now={props.now} tenantId={props.config.tenantId} />
      </PatientKeypairProvider>
    </PinningProvider>
  );
}

/**
 * Token-Decode + Pinning innerhalb des Route-Kontexts.
 *
 * Reihenfolge:
 *  1. Token aus `useParams` lesen und dekodieren (`useTokenFromRoute`).
 *  2. Bei Decode-Fehler: State-Machine `TOKEN_INVALID` dispatchen, sofort
 *     den Technical-Error-Generic-Screen rendern — kein Pinning, weil ohne
 *     gültigen Token kein Fingerprint zum Vergleich existiert.
 *  3. Bei Erfolg: `pinFingerprint`-Bytes → Hex-Lowercase und in den Pinning-
 *     Bootstrap geben.
 *  4. Pinning-Fehler: `PINNING_FAILED` dispatchen, Pinning-Failure-Screen.
 *  5. Pinning-Erfolg: `<PinningProvider>` + Keypair-Provider, dann `CancelApp`.
 */
function RouteScopedPinnedApp(props: PinnedAppProps): JSX.Element {
  const machine = useCancelMachine();
  const now = props.now ?? Date.now;
  const tokenResult = useTokenFromRoute({ nowMs: now() });

  if (tokenResult.status === 'error') {
    return <TokenInvalidGate machine={machine} tenantId={props.config.tenantId} />;
  }

  const fingerprintHex = bytesToHexLower(tokenResult.decoded.pinFingerprint);
  return (
    <PinningGate
      config={props.config}
      machine={machine}
      decoded={tokenResult.decoded}
      fingerprintHex={fingerprintHex}
      now={now}
      {...(props.verifyPinning ? { verifyPinning: props.verifyPinning } : {})}
    />
  );
}

/**
 * Catch-all für alle Pfade außerhalb von `/cancel/:token` (z. B. `/admin`,
 * `/`): rendert „Seite nicht gefunden“ im regulären Seitenrahmen. Kein
 * Redirect — die URL bleibt stehen, damit Patient:innen (und Support) sehen,
 * was eingegeben wurde. Die State-Machine ist hier nicht beteiligt.
 */
function NotFoundRoute(): JSX.Element {
  return (
    <AppShell>
      <ScreenShell motionKey="not-found">
        <NotFoundScreen />
      </ScreenShell>
    </AppShell>
  );
}

/**
 * Routen-Layout: `/cancel/:token` rendert Token-Decode, Pinning und die
 * State-Machine. Alle anderen Pfade zeigen „Seite nicht gefunden“ — ohne
 * Redirect und ohne Technical-Error, weil kein Absage-Flow gestartet wurde.
 */
export function CancelRoutes(props: PinnedAppProps): JSX.Element {
  return (
    <Routes>
      <Route path="/cancel/:token" element={<RouteScopedPinnedApp {...props} />} />
      <Route path="*" element={<NotFoundRoute />} />
    </Routes>
  );
}

/**
 * Mock-spezifische Routes ohne Pinning. Rendert `CancelApp` direkt — der
 * Demo-Switcher übernimmt die Screen-Steuerung, ein verifiedKey ist im
 * Mock-Pfad bewusst `null`.
 */
export function MockRoutes(props: {
  readonly machine: CancelMachine;
  readonly now: NowFn;
  readonly tenantId: string;
}): JSX.Element {
  return (
    <Routes>
      <Route
        path="/cancel/:token"
        element={<CancelApp machine={props.machine} now={props.now} tenantId={props.tenantId} />}
      />
      <Route path="*" element={<NotFoundRoute />} />
    </Routes>
  );
}
